import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	MenuItem,
	IconButton,
	Typography,
	Box,
	Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

interface Invoice {
	type: string;
	group: string;
	subGroup: string;
}

export interface InvoiceData {
	hoa: Record<string, Invoice>;
	property: Record<string, Invoice>;
	event: Record<string, Invoice>;
}

interface InvoiceManagerProps {
	mode: string;
	data?: InvoiceData | null;
	invoiceData: InvoiceData;
	setInvoiceData: (value: any) => void;
	organization:string | null;
}

const InvoiceManager: React.FC<InvoiceManagerProps> = ({
	mode,
	data,
	invoiceData,
	setInvoiceData,
	organization
}) => {
	const [open, setOpen] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<keyof InvoiceData | "">("");
	const [expanded, setExpanded] = useState<string | false>(false);
	const [formValues, setFormValues] = useState({
		key: "",
		type: "",
		group: "",
		subGroup: "",
	});
	const [editMode, setEditMode] = useState(false);

	useEffect(() => {
		if (mode == "update" && data !== null) {
			setInvoiceData({ ...data });
		}
	}, []);

	useEffect(() => {
		if (open === true && !editMode) {
			setSelectedCategory("");
			setFormValues({ key: "", type: "", group: "", subGroup: "" });
		}
	}, [open]);

	const handleOpen = useCallback(
		(category: keyof InvoiceData, key?: string) => {
			setSelectedCategory(category);
			if (key) {
				setEditMode(true);
				setFormValues({ key, ...invoiceData[category][key] });
			} else {
				setEditMode(false);
				setFormValues({ key: "", type: "", group: "", subGroup: "" });
			}
			setOpen(true);
		},
		[invoiceData]
	);

	const handleClose = useCallback(() => {
		setOpen(false);
		setEditMode(false);
	}, []);

	const handleSave = useCallback(() => {
		if (!selectedCategory || !formValues.key) return;
		if (
			!editMode &&
			Object.keys(invoiceData[selectedCategory]).some(
				(existingKey) => existingKey === formValues.key
			)
		) {
			alert("Key already exists!");
			return;
		}

		setExpanded(selectedCategory);

		setInvoiceData((prev: any) => ({
			...prev,
			[selectedCategory]: {
				...prev[selectedCategory],
				[formValues.key]: {
					type: formValues.type,
					group: formValues.group,
					subGroup: formValues.subGroup,
				},
			},
		}));
		handleClose();
	}, [selectedCategory, formValues, editMode, invoiceData, handleClose]);

	const handleDelete = useCallback((category: keyof InvoiceData, key: string) => {
		setInvoiceData((prev: any) => {
			const newCategoryData = { ...prev[category] };
			delete newCategoryData[key];
			return { ...prev, [category]: newCategoryData };
		});
	}, []);

	const handleAccordionChange = useCallback(
		(category: string) => {
			setExpanded(expanded === category ? false : category);
		},
		[expanded]
	);

	const handleFormChange = useCallback((field: keyof typeof formValues, value: string) => {
		setFormValues((prev) => ({
			...prev,
			[field]: value,
		}));
	}, []);

	const categories = useMemo(() => ["hoa", "property", "event"] as (keyof InvoiceData)[], []);

	return (
		<Stack flexDirection={"column"} gap={2} p={1}>
			<Stack flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
				<Typography fontSize={'16px'} color={'black'} variant="body2"><strong>Organization name:</strong> {organization}</Typography>
				<Button sx={{ alignSelf: "end" }} variant="contained" onClick={() => setOpen(true)}>
					Add New Mapping
				</Button>
			</Stack>
			{categories.map((category) => (
				<InvoiceCategory
					key={category}
					category={category}
					invoiceData={invoiceData}
					expanded={expanded}
					onAccordionChange={handleAccordionChange}
					onEdit={handleOpen}
					onDelete={handleDelete}
				/>
			))}

			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{editMode ? "Edit Mapping" : "Add Mapping"}</DialogTitle>
				<DialogContent>
					{!editMode && (
						<TextField
							select
							fullWidth
							label="Category"
							value={selectedCategory}
							onChange={(e) =>
								setSelectedCategory(e.target.value as keyof InvoiceData)
							}
							margin="dense"
						>
							{categories.map((cat) => (
								<MenuItem key={cat} value={cat}>
									{cat.toUpperCase()}
								</MenuItem>
							))}
						</TextField>
					)}
					<TextField
						fullWidth
						label="Key"
						value={formValues.key}
						onChange={(e) => handleFormChange("key", e.target.value)}
						margin="dense"
						disabled={editMode}
					/>
					<TextField
						fullWidth
						label="Type"
						value={formValues.type}
						onChange={(e) => handleFormChange("type", e.target.value)}
						margin="dense"
					/>
					<TextField
						fullWidth
						label="Group"
						value={formValues.group}
						onChange={(e) => handleFormChange("group", e.target.value)}
						margin="dense"
					/>
					<TextField
						fullWidth
						label="SubGroup"
						value={formValues.subGroup}
						onChange={(e) => handleFormChange("subGroup", e.target.value)}
						margin="dense"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Cancel</Button>
					<Button onClick={handleSave} variant="contained" color="primary">
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

export default InvoiceManager;

interface InvoiceCategoryProps {
	category: keyof InvoiceData;
	invoiceData: InvoiceData;
	expanded: string | false;
	onAccordionChange: (category: string) => void;
	onEdit: (category: keyof InvoiceData, key: string) => void;
	onDelete: (category: keyof InvoiceData, key: string) => void;
}

const InvoiceCategory = React.memo(
	({
		category,
		invoiceData,
		expanded,
		onAccordionChange,
		onEdit,
		onDelete,
	}: InvoiceCategoryProps) => {
		const invoiceEntries = useMemo(() => {
			return Object.entries(invoiceData[category]);
		}, [category, invoiceData]);

		return (
			<Accordion
				expanded={expanded === category}
				onChange={() => onAccordionChange(category)}
				disableGutters
				style={{
					backgroundColor: "#FAFAFA",
					boxShadow: "none",
				}}
			>
				<AccordionSummary
					sx={{ borderBottom: "1px solid #E0E0E0" }}
					expandIcon={<ExpandMoreIcon />}
				>
					<Typography>
						{category.toUpperCase()} ({invoiceEntries.length})
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{ background: "white" }}>
					<Stack gap={1}>
						{invoiceEntries.map(([key, invoice]) => (
							<InvoiceItem
								key={key}
								category={category}
								keyName={key}
								invoice={invoice}
								onEdit={onEdit}
								onDelete={onDelete}
							/>
						))}
					</Stack>
				</AccordionDetails>
			</Accordion>
		);
	}
);

interface InvoiceItemProps {
	category: keyof InvoiceData;
	keyName: string;
	invoice: Invoice;
	onEdit: (category: keyof InvoiceData, key: string) => void;
	onDelete: (category: keyof InvoiceData, key: string) => void;
}

const InvoiceItem = React.memo(
	({ category, keyName, invoice, onEdit, onDelete }: InvoiceItemProps) => {
		return (
			<Stack
				p={1}
				borderRadius={"4px"}
				flexDirection={"row"}
				alignItems={"center"}
				justifyContent={"space-between"}
				border={"1px solid #E0E0E0"}
			>
				<Stack flexDirection={"column"}>
					<Typography>
						<strong>Key:</strong> {keyName}
					</Typography>
					<Typography>
						<strong>Group:</strong> {invoice.group}
					</Typography>
					<Typography>
						<strong>SubGroup:</strong> {invoice.subGroup}
					</Typography>
					<Typography>
						<strong>Type:</strong> {invoice.type}
					</Typography>
				</Stack>
				<Stack flexDirection={"column"}>
					<IconButton onClick={() => onEdit(category, keyName)}>
						<EditIcon />
					</IconButton>
					<IconButton onClick={() => onDelete(category, keyName)}>
						<DeleteIcon />
					</IconButton>
				</Stack>
			</Stack>
		);
	}
);
